/**
 * Access to the Lighthouse desktop app (Electron) from the hosted web app.
 *
 * The desktop shell's preload exposes `window.lighthouseDesktop` in the main
 * window, only on the Lighthouse origin. Everything desktop-only goes through
 * `getDesktopBridge()` so the web app and the Capacitor app never see a
 * difference: without a bridge every wrapper here is a no-op.
 *
 * `isDesktopShell()` (utils/platform) is the coarser check: it is also true
 * for an Electron user agent without the bridge (an older shell), which is
 * enough for route scope but not for anything that calls into the shell.
 */
import { BRIDGE_VERSION, type DesktopFile, type DesktopNotification, type LighthouseDesktopBridge } from './types'

export type DesktopOs = 'mac' | 'win' | 'linux'

/** The shell's bridge, or null on the web / Capacitor / an incompatible shell. */
export function getDesktopBridge(): LighthouseDesktopBridge | null {
    if (typeof window === 'undefined') return null
    const bridge = window.lighthouseDesktop
    if (!bridge || typeof bridge !== 'object') return null
    // Additions stay optional; only a major (breaking) bump below what we speak is refused.
    if (!(Number(bridge.bridgeVersion) >= BRIDGE_VERSION)) return null
    return bridge
}

/** True inside the Lighthouse desktop app with a usable bridge. */
export function isDesktopApp(): boolean {
    return getDesktopBridge() !== null
}

/** `mac` / `win` / `linux` inside the desktop app, null everywhere else. */
export function desktopPlatform(): DesktopOs | null {
    const bridge = getDesktopBridge()
    if (!bridge) return null
    return toDesktopOs(bridge.platform)
}

export function toDesktopOs(platform: string | null | undefined): DesktopOs {
    if (platform === 'darwin') return 'mac'
    if (platform === 'win32') return 'win'
    return 'linux'
}

/** Desktop app version (e.g. `0.2.0`), null outside the app. */
export function desktopAppVersion(): string | null {
    return getDesktopBridge()?.version ?? null
}

/** Run `fn` against the bridge; swallow shell errors (a desktop nicety must never break the page). */
function withBridge<T>(fn: (bridge: LighthouseDesktopBridge) => T): T | undefined {
    const bridge = getDesktopBridge()
    if (!bridge) return undefined
    try {
        return fn(bridge)
    } catch (err) {
        console.warn('[desktop] bridge call failed', err)
        return undefined
    }
}

export function desktopNotify(notification: DesktopNotification): void {
    withBridge((b) => b.notify(notification))
}

export function desktopSetBadge(count: number): void {
    withBridge((b) => b.setBadge(Math.max(0, Math.floor(count) || 0)))
}

export function desktopRequestAttention(): void {
    withBridge((b) => b.requestAttention())
}

/** Open a URL in the system browser (falls back to a new tab on the web). */
export function openExternal(url: string): void {
    const handled = withBridge((b) => {
        b.files.openExternal(url)
        return true
    })
    if (!handled && typeof window !== 'undefined') window.open(url, '_blank', 'noopener')
}

/** Open the desktop app's own Settings window (no-op on the web). */
export function openDesktopSettings(section?: string): void {
    withBridge((b) => b.settings.open(section))
}

/** Open Quick Ask, optionally prefilled. */
export function openQuickAsk(prefill?: { text?: string; files?: DesktopFile[] }): void {
    withBridge((b) => b.ask.open(prefill))
}

/** Start meeting notes from the app (the shell owns capture). Resolves false outside the app. */
export async function startDesktopMeeting(options?: { title?: string; eventId?: string }): Promise<boolean> {
    const bridge = getDesktopBridge()
    if (!bridge) return false
    await bridge.meetings.start(options)
    return true
}

/** A region screenshot from the shell as a File (null when cancelled or outside the app). */
export async function captureScreenshotFile(mode: 'region' | 'screen' = 'region'): Promise<File | null> {
    const bridge = getDesktopBridge()
    if (!bridge) return null
    const shot = await bridge.capture.screenshot({ mode })
    return shot ? desktopFileToFile(shot) : null
}

/** Bytes from the bridge → a browser File. */
export function desktopFileToFile(file: DesktopFile): File {
    return new File([file.data], file.name || 'file', { type: file.type || 'application/octet-stream' })
}

export function desktopFilesToFiles(files: DesktopFile[] | null | undefined): File[] {
    return (files ?? []).filter((f) => f && f.data).map(desktopFileToFile)
}
